/** Presentation helpers shared by the app and by server-side exports. */

const LOCALE = 'sv-SE';

const currencyFormatters = new Map<string, Intl.NumberFormat>();

/** `1234.5` → `1 234,50 kr` */
export function formatMoney(value: number | null | undefined, currency = 'SEK'): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–';
  let formatter = currencyFormatters.get(currency);
  if (!formatter) {
    formatter = new Intl.NumberFormat(LOCALE, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    currencyFormatters.set(currency, formatter);
  }
  return formatter.format(value);
}

/** Like {@link formatMoney} but without the currency suffix, for dense tables. */
export function formatAmount(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–';
  return value.toLocaleString(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Trims trailing zeros: `1` → `1`, `0.412` → `0,412`. */
export function formatQuantity(value: number): string {
  return value.toLocaleString(LOCALE, { maximumFractionDigits: 3 });
}

/**
 * Formats a naive local ISO string (see `parseLocalDateTime`) for display.
 * Deliberately string-based so no timezone conversion can creep in.
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '–';
  return iso.slice(0, 10);
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '–';
  const [date, time] = iso.split('T');
  if (!time) return date ?? '–';
  return `${date} ${time.slice(0, 5)}`;
}

/** `2024-03-15` → `mars 2024`, for month grouping headers. */
export function formatMonth(iso: string | null | undefined): string {
  if (!iso) return 'Utan datum';
  const [yearText, monthText] = iso.slice(0, 7).split('-');
  const year = Number(yearText);
  const month = Number(monthText);
  if (!year || !month) return 'Utan datum';
  const label = new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(LOCALE, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** `st` → `st`, `other` → `` — units are appended after a quantity. */
export function formatUnit(unit: string): string {
  return unit === 'other' ? '' : unit;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * A timestamp as a short localized date and time, e.g. `2024-03-15 14:30`.
 * Returns `null` for a falsy `value` so callers can supply their own
 * "never happened" copy instead of a generic one.
 */
export function formatDateTimeShort(value: string | number | null | undefined): string | null {
  if (!value) return null;
  return new Intl.DateTimeFormat(LOCALE, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
}

/** Coarse "for han sedan" style relative time, used for sync status. */
export function formatRelativeTime(timestamp: number | null): string {
  if (!timestamp) return 'aldrig';
  const seconds = Math.round((Date.now() - timestamp) / 1000);
  if (seconds < 45) return 'nyss';
  if (seconds < 90) return 'för en minut sedan';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `för ${minutes} min sedan`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `för ${hours} tim sedan`;
  const days = Math.round(hours / 24);
  if (days < 30) return `för ${days} dagar sedan`;
  return new Date(timestamp).toLocaleDateString(LOCALE);
}

/**
 * A `Date` as a naive local `YYYY-MM-DD`.
 *
 * Deliberately built from the local getters rather than `toISOString()`, which
 * would convert to UTC and hand back yesterday's date for anyone east of
 * Greenwich late in the evening.
 */
export function toLocalIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Today, as a naive local `YYYY-MM-DD`. */
export function localDateToday(): string {
  return toLocalIsoDate(new Date());
}

const COMPACT_UNITS = ['', ' k', ' M', ' G', ' T'] as const;

/**
 * A count in large-number notation: `999` → `999`, `1000` → `1 k`,
 * `1234` → `1,2 k`, `100000` → `100 k`, `10000000` → `10 M`. Up to one
 * decimal, trailing zeros dropped. `decimal` is the separator, a comma for
 * Swedish.
 */
export function formatCompactCount(value: number, decimal = ','): string {
  if (!Number.isFinite(value)) return '–';
  const sign = value < 0 ? '-' : '';
  let scaled = Math.abs(value);
  let unit = 0;
  while (scaled >= 1000 && unit < COMPACT_UNITS.length - 1) {
    scaled /= 1000;
    unit += 1;
  }
  let rounded = unit === 0 ? Math.round(scaled) : Math.round(scaled * 10) / 10;
  // 999 995 rounds to 1000 k, which is 1 M.
  if (rounded >= 1000 && unit < COMPACT_UNITS.length - 1) {
    rounded = 1;
    unit += 1;
  }
  return `${sign}${String(rounded).replace('.', decimal)}${COMPACT_UNITS[unit]}`;
}
